import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaBuilding,
  FaPhone,
  FaArrowLeft,
} from "react-icons/fa";
import { API_BASE_URL } from "../environment";
import {API} from "../common/Constants";
import { analyticsService } from "../services/analyticsService";

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    company: "",
    phone: "",
    agreeTerms: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
    emailSent: false,
    errorMessage: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!formData.company.trim())
      errors.company = "Institution name is required";

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^\+?[1-9]\d{9,14}$/.test(formData.phone.replace(/\s+/g, ""))) {
      errors.phone = "Please enter a valid phone number";
    }

    if (!formData.agreeTerms) {
      errors.agreeTerms = "You must agree to the terms and conditions";
    }

    return errors;
  };

  const handleResendVerification = async () => {
    try {
      setFormStatus((prev) => ({ ...prev, submitting: true }));
      await axios.post(`${API_BASE_URL}${API.RESEND_VERIFICATION}`, {
        email: formData.email,
      });
      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        emailSent: true,
        errorMessage: "",
      }));
    } catch (error) {
      console.error("Error resending verification email:", error);
      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        error: true,
        errorMessage: "Failed to resend verification email. Please try again.",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormStatus({
      submitting: true,
      submitted: false,
      error: false,
      emailSent: false,
      errorMessage: "",
    });

    try {
      // Prepare the data according to RegisterRequestDto
      const registerData = {
        email: formData.email,
        password: formData.password,
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        institutionName: formData.company,
        phone: formData.phone,
      };

      // Call the registration API
      await axios.post(`${API_BASE_URL}${API.REGISTER}`, registerData);

      // Track successful registration event
      analyticsService.trackEvent(
        'User',
        'Registration',
        `Institution: ${formData.company}`,
        1
      );

      // If successful, update UI to show verification needed
      setFormStatus({
        submitting: false,
        submitted: true,
        error: false,
        emailSent: true,
        errorMessage: "",
      });
    } catch (error) {
      console.error("Registration error:", error);

      // Handle different error types
      let errorMessage = "Registration failed. Please try again.";

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 409) {
          errorMessage =
            "Email address already registered. Please use a different email.";
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      setFormStatus({
        submitting: false,
        submitted: false,
        error: true,
        emailSent: false,
        errorMessage: errorMessage,
      });
    }
  };

  return (
    <section className="register-page section-padding">
      <div className="container">
        <div className="section-header">
          <h2>Create an Account</h2>
          <p>Register your educational institution with Pathshala Sanchalak</p>
          <div className="required-field-note">
            <span className="required-asterisk">*</span> indicates required
            field
          </div>
        </div>

        <div className="register-content">
          <div className="register-form-container">
            <Link to="/" className="back-link">
              <FaArrowLeft /> Back to Home
            </Link>

            {formStatus.submitted && formStatus.emailSent ? (
              <div className="success-message">
                <h3>Registration Initiated!</h3>
                <p>
                  Thank you for registering with Pathshala Sanchalak. We've sent
                  a verification email to <strong>{formData.email}</strong>.
                </p>
                <p>
                  Please check your email and click on the verification link to
                  complete your registration.
                </p>
                <div className="resend-verification">
                  <p>Didn't receive an email?</p>
                  <button
                    className="btn btn-secondary"
                    onClick={handleResendVerification}
                    disabled={formStatus.submitting}
                  >
                    {formStatus.submitting
                      ? "Sending..."
                      : "Resend Verification Email"}
                  </button>
                </div>
              </div>
            ) : (
              <form className="register-form" onSubmit={handleSubmit}>
                {formStatus.error && (
                  <div className="error-alert">
                    <p>{formStatus.errorMessage}</p>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">
                      <FaUser /> First Name{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={formErrors.firstName ? "error" : ""}
                    />
                    {formErrors.firstName && (
                      <span className="error-message">
                        {formErrors.firstName}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">
                      <FaUser /> Last Name{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={formErrors.lastName ? "error" : ""}
                    />
                    {formErrors.lastName && (
                      <span className="error-message">
                        {formErrors.lastName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <FaEnvelope /> Email Address{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={formErrors.email ? "error" : ""}
                  />
                  {formErrors.email && (
                    <span className="error-message">{formErrors.email}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">
                      <FaLock /> Password{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={formErrors.password ? "error" : ""}
                    />
                    {formErrors.password && (
                      <span className="error-message">
                        {formErrors.password}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">
                      <FaLock /> Confirm Password{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={formErrors.confirmPassword ? "error" : ""}
                    />
                    {formErrors.confirmPassword && (
                      <span className="error-message">
                        {formErrors.confirmPassword}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">
                      <FaBuilding /> Institution Name{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={formErrors.company ? "error" : ""}
                    />
                    {formErrors.company && (
                      <span className="error-message">
                        {formErrors.company}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <FaPhone /> Phone Number{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 1234567890"
                      className={formErrors.phone ? "error" : ""}
                    />
                    {formErrors.phone && (
                      <span className="error-message">{formErrors.phone}</span>
                    )}
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleChange}
                      className={formErrors.agreeTerms ? "error" : ""}
                    />
                    <label htmlFor="agreeTerms">
                      I agree to the{" "}
                      <Link to="/terms-and-conditions" className="text-link">
                        Terms and Conditions
                      </Link>{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                  </div>
                  {formErrors.agreeTerms && (
                    <span className="error-message">
                      {formErrors.agreeTerms}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary register-btn"
                  disabled={formStatus.submitting}
                >
                  {formStatus.submitting ? "Registering..." : "Register Now"}
                </button>

                <div className="login-link">
                  Already have an account?{" "}
                  <Link to="/login" className="text-link">
                    Log In
                  </Link>
                </div>
              </form>
            )}
          </div>

          <div className="register-benefits">
            <h3>Benefits of Registration</h3>
            <ul>
              <li>
                <div className="benefit-icon">
                  <FaUser />
                </div>
                <div className="benefit-text">
                  <h4>Administrative Dashboard</h4>
                  <p>
                    Access a comprehensive dashboard to manage your entire
                    educational institution
                  </p>
                </div>
              </li>
              <li>
                <div className="benefit-icon">
                  <FaEnvelope />
                </div>
                <div className="benefit-text">
                  <h4>Communication Tools</h4>
                  <p>
                    Connect with students, parents and faculty through
                    integrated communication channels
                  </p>
                </div>
              </li>
              <li>
                <div className="benefit-icon">
                  <FaBuilding />
                </div>
                <div className="benefit-text">
                  <h4>Institution Management</h4>
                  <p>
                    Streamline operations with tools specifically designed for
                    educational institutions
                  </p>
                </div>
              </li>
              <li>
                <div className="benefit-icon">
                  <FaLock />
                </div>
                <div className="benefit-text">
                  <h4>Data Security</h4>
                  <p>
                    Protect sensitive student and institutional data with our
                    advanced security features
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Register;
